import React, { useEffect, useMemo, useState } from 'react';
import { SingleNumber } from 'utils/api/models/numbers/singleNumber';
import CustomInput from 'components/CustomInput/CustomInput';
import { translate } from 'components/Translation';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import CustomToggle from 'components/CustomToggle/CustomToggle';
import Table from 'components/CustomTable/CustomTable';
import { DepartmentsCell, PhoneNumberCell, UserNameCell } from 'components/Cells/Cells';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import styles from 'assets/jss/material-dashboard-pro-react/components/addNumberDialogStyle';
import { makeStyles } from '@material-ui/core';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IDialogBodyProps {
	numbers: Array<types.organizations.INumber>;
	searchInput: string;
	setSearchInput: React.Dispatch<React.SetStateAction<string>>;
	notAssignedToDepartment: boolean;
	setNotAssignedToDepartment: React.Dispatch<React.SetStateAction<boolean>>;
	setSelectedNumbers: React.Dispatch<React.SetStateAction<any>>;
	loading: boolean;
}

const DialogBody = ({
	numbers,
	searchInput,
	setSearchInput,
	notAssignedToDepartment,
	setNotAssignedToDepartment,
	setSelectedNumbers,
	loading,
}: IDialogBodyProps) => {
	const classes = useStyles();
	const [selectedIndexes, setSelectedIndexes] = useState([]);
	const [filteredBySearchInput, setFilteredBySearchInput] = useState<Array<types.organizations.INumber>>([]);

	useEffect(() => {
		if (!loading && numbers.length > 0) {
			const lowercaseSearchInput = searchInput.toLowerCase();
			setSelectedIndexes([]);
			const filteredNumbers = numbers.filter((number: types.organizations.INumber) =>
				[number.accountNum, number.username].some((element) => {
					if (element) {
						return element.toLowerCase().indexOf(lowercaseSearchInput) >= 0;
					}
					return false;
				})
			);
			setFilteredBySearchInput(filteredNumbers);
		}
	}, [searchInput, numbers, loading]);

	const filteredByDepartment = useMemo(() => {
		setSelectedIndexes([]);
		return notAssignedToDepartment
			? filteredBySearchInput.filter((number: SingleNumber) => !number.departmentId)
			: filteredBySearchInput;
	}, [notAssignedToDepartment, filteredBySearchInput]);

	useEffect(() => {
		const selectedNumbers = selectedIndexes.map((selectedIndex) => filteredByDepartment[selectedIndex]);
		setSelectedNumbers(() => selectedNumbers || []);
		// eslint-disable-next-line
	}, [selectedIndexes, filteredByDepartment]);

	return (
		<div className={classes.dialogBody}>
			<div className={classes.filters}>
				<div>
					<CustomInput
						labelText={translate('departments.number.users')}
						formControlProps={{
							fullWidth: true,
						}}
						inputProps={{
							value: searchInput,
							onChange: (event: React.FormEvent<HTMLInputElement>) =>
								setSearchInput((event.target as HTMLInputElement).value),
							endAdornment: (
								<InputAdornment position="end">
									<SearchIcon className={classes.searchIcon} />
								</InputAdornment>
							),
						}}
					/>
				</div>
				<div>
					<CustomToggle
						value="showOnlyAssignedToDepartment"
						checked={notAssignedToDepartment}
						onChange={() =>
							setNotAssignedToDepartment((notAssignedToDepartment: boolean) => !notAssignedToDepartment)
						}
						label={translate('departments.only.not.assigned')}
						formControlProps={{ className: classes.switch }}
					/>
				</div>
			</div>
			<Table
				withCheckbox
				total={filteredByDepartment.length}
				onSelect={(value) => setSelectedIndexes(value)}
				selected={selectedIndexes}
				tableProps={{
					data: filteredByDepartment,
					loading,
					columns: [
						{
							Header: translate('table.phone.number'),
							Cell: ({ original }: { original: SingleNumber }) => (
								<PhoneNumberCell text={original.accountNum} />
							),
							accessor: 'accountNum',
						},
						{
							Header: translate('table.user.name'),
							width: 160,
							Cell: ({ original }: { original: SingleNumber }) => (
								<UserNameCell text={original.username} type="MANAGER" />
							),
							accessor: 'username',
						},
						!notAssignedToDepartment && {
							Header: translate('numbers.department'),
							id: 'department',
							width: 130,
							Cell: ({ original: { department } }: { original: { department: SingleDepartment } }) => {
								return <DepartmentsCell departments={department ? [department] : []} />;
							},
						},
					].filter(Boolean),
					defaultPageSize: 5,
				}}
			/>
		</div>
	);
};

export default DialogBody;
